import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Info } from "lucide-react";
import HealthDetailDrawer from "../Components/HealthDetailDrawer";
import { GlobalContext } from "../Context/GlobalContext";
import { images } from "../Assets/images";
import { normalizeScanDetails } from "./scanDetails/scanNormalizer";
import {
  styleKeyFromCategory,
  statusIconMap,
  getCategoryStyle,
} from "../utils";

const normalizeKey = (str) =>
  (str || "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const BIOMARKER_FILTER = {
  CV: [
    "blood_pressure", "heart_rate", "oxygen_saturation", "hrv_sdnn",
    "pulse_rate_quotient", "vo2_max", "mean_arterial_pressure", "cardiac_workload",
    "pulse_pressure", "anemia_index", "stress_level", "normalized_stress_index",
    "mean_rri", "rmssd", "recovery_ability_pns_zone", "pns_index", "sns_index",
    "stress_response_sns_zone", "sd1", "sd2",
    "high_blood_pressure_risk", "ascvd_risk", "hypertension_risk_score",
  ],
  Metabolic: [
    "average_blood_glucose", "anemia_index", "glucose_management_indicator",
    "body_mass_index", "waist_to_height_ratio", "body_shape_index",
    "high_total_cholesterol_risk",
  ],
  Immune: [
    "stress_level", "normalized_stress_index", "rmssd",
    "recovery_ability_pns_zone", "stress_response_sns_zone", "sns_index",
    "sd2", "lf_hf", "inflammation_index", "metabolic_efficiency_ratio",
  ],
  Sugar: [
    "average_blood_glucose", "hba1c", "glucose_management_indicator",
    "body_mass_index", "waist_to_height_ratio", "body_shape_index",
    "high_fasting_glucose_risk", "high_hba1c_risk",
  ],
  Stress: [
    "stress_level", "stress_index", "normalized_stress_index", "mean_rri",
    "rmssd", "recovery_ability_pns_zone", "pns_index",
    "stress_response_sns_zone", "sns_index", "sd1", "sd2", "lf_hf",
  ],
};

const filterOrder = ["CV", "Metabolic", "Immune", "Sugar", "Stress"];

const filterToStyleKey = {
  CV: "heart",
  Metabolic: "body",
  Immune: "risk",
  Sugar: "blood",
  Stress: "stress",
};

const filterDisplayLabels = {
  CV: "Cardiovascular",
  Metabolic: "Metabolic",
  Immune: "Immune",
  Sugar: "Sugar",
  Stress: "Stress",
};

const filterCategoriesByBiomarker = (() => {
  const map = new Map();
  Object.entries(BIOMARKER_FILTER).forEach(([cat, keys]) => {
    keys.forEach((k) => {
      const nk = normalizeKey(k);
      if (!map.has(nk)) map.set(nk, new Set());
      map.get(nk).add(cat);
    });
  });
  return map;
})();

const ShowAllResult2 = () => {
  const {
    scanDetails,
    translatedLabels,
    getScanDetails,
    wellnessLabel,
    is4Life,
  } = useContext(GlobalContext);
  const location = useLocation();
  const scanId = location.state?.scanId;
  const screenLabels = translatedLabels?.common;

  const initialFilter = location.state?.selectedFilter || "all";
  const initialFilterKey = normalizeKey(initialFilter);

  const [showCardDetail, setShowCardDetail] = useState(false);
  const [activeTab, setActiveTab] = useState(initialFilterKey);
  const [selectedMetricData, setSelectedMetricData] = useState(null);
  const [activePill, setActivePill] = useState(
    filterOrder.includes(initialFilter) ? initialFilter : "all",
  );

  const normalizedScanDetailss = useMemo(
    () => normalizeScanDetails(scanDetails, screenLabels),
    [scanDetails, screenLabels],
  );

  useEffect(() => {
    getScanDetails(scanId || "");
  }, [getScanDetails]);

  useEffect(() => {
    if (showCardDetail) {
      window.history.pushState({ drawerOpen: true }, "");
      const handlePopState = () => {
        if (showCardDetail) setShowCardDetail(false);
      };
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
        if (window.history.state?.drawerOpen) window.history.back();
      };
    }
  }, [showCardDetail]);

  const allMetricCards = useMemo(() => {
    if (!normalizedScanDetailss || normalizedScanDetailss.length === 0)
      return [];

    const cards = [];
    normalizedScanDetailss.forEach((categoryData) => {
      const sourceCategory = categoryData.category;
      if (normalizeKey(sourceCategory) === "wellness") return;

      categoryData.metrics.forEach((metric) => {
        if (metric.value === undefined || metric.value === null || metric.value === "") return;

        const matchKey = normalizeKey(metric.key);
        const filterCats = filterCategoriesByBiomarker.get(matchKey);
        if (!filterCats || filterCats.size === 0) return;

        cards.push({
          id: `${sourceCategory}-${metric.key}`,
          key: sourceCategory,
          filterCategories: Array.from(filterCats),
          title: metric.label || metric.key,
          category: metric.label || metric.key,
          categoryScore: metric.value,
          avgReadingScore: metric.avg || "-",
          rawMetric: {
            key: metric.key,
            value: metric.value,
            cronologicalAge: metric.cronologicalAge,
            unit: metric.unit || "",
            avg: metric.avg || "",
            comment: metric.label || metric.key,
            status: metric.status || "Normal",
            normal: metric.normal || "N/A",
          },
        });
      });
    });

    return cards;
  }, [normalizedScanDetailss]);

  const visibleCards = useMemo(() => {
    if (activePill === "all") return allMetricCards;
    return allMetricCards.filter((c) => c.filterCategories.includes(activePill));
  }, [allMetricCards, activePill]);

  const groupedCards = useMemo(() => {
    if (activePill !== "all") {
      return [[activePill, visibleCards]];
    }
    const grouped = {};
    filterOrder.forEach((cat) => {
      grouped[cat] = [];
    });
    visibleCards.forEach((card) => {
      const cat = card.filterCategories[0];
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(card);
    });
    return filterOrder
      .map((cat) => [cat, grouped[cat] || []])
      .filter(([, list]) => list.length > 0);
  }, [visibleCards, activePill]);

  const availablePills = filterOrder;

  const renderCard = (card, parentCat) => {
    const styleSource = filterToStyleKey[parentCat] || card.key;
    const { icon, color } = getCategoryStyle(styleSource);
    const isHeartAge = card.rawMetric?.key === "heart_age";

    return (
      <div
        key={card.id}
        className={`card ${
          is4Life
            ? `fourlife-shadow-${styleKeyFromCategory(styleSource)}`
            : `shadow-${styleKeyFromCategory(styleSource)}`
        } d-flex flex-column px-2 py-2 mb-3`}
        style={{ cursor: "pointer" }}
        onClick={() => {
          setActiveTab(normalizeKey(card.key));
          setSelectedMetricData({
            ...card.rawMetric,
            source: is4Life ? "show_all_result" : "",
            icon: statusIconMap[card.rawMetric.status] || images.normalIcon,
          });
          setShowCardDetail(true);
        }}
      >
        <div className="d-flex flex-row justify-content-between w-100">
          <div className={`icon-wrapper ${color}`}>
            <img
              src={icon}
              alt={card.category}
              style={{ width: "24px", height: "24px" }}
            />
          </div>
          <div className="flex-grow-1 px-2">
            <h6 className="mb-0 fw-semibold welless-card-title">
              {card.title}
            </h6>
            <div className="d-flex justify-content-between bp-details">
              <div>
                <p className="mb-0 font-bold fs-14px">
                  {isHeartAge
                    ? screenLabels?.cronological_age?.label_value ||
                      "Chronological Age"
                    : screenLabels?.reading?.label_value || "Current Reading"}
                </p>
                <p className="mb-0 fs-14px fw-semibold">
                  {isHeartAge
                    ? card.rawMetric.cronologicalAge || "-"
                    : card.categoryScore}
                </p>
              </div>
              <div>
                <p className="mb-0 fs-14px text-end">
                  {isHeartAge
                    ? screenLabels?.biological_age?.label_value ||
                      "Biological Age"
                    : wellnessLabel || "-"}
                </p>
                <p className="mb-0 fs-14px fw-semibold">
                  {isHeartAge
                    ? card.categoryScore
                    : card.avgReadingScore || "-"}
                </p>
              </div>
            </div>
          </div>
          <div className="info-icon me-1">
            <Info size={18} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="content-part pt-5">
      <div
        className="d-flex gap-2 align-items-center flex-nowrap overflow-auto py-3 mb-2"
        style={{
          position: "sticky",
          top: "0px",
          zIndex: 10,
          background: "var(--pageBgColor, #fff)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <button
          onClick={() => setActivePill("all")}
          className={`btn btn-sm rounded-pill px-3 py-1 ${
            activePill === "all" ? "btn-dark" : "btn-outline-secondary"
          }`}
          style={{ whiteSpace: "nowrap", fontSize: "13px" }}
        >
          {screenLabels?.all?.label_value || "All"}
        </button>

        {availablePills.map((cat) => (
          <button
            key={cat}
            onClick={() => setActivePill(cat)}
            className={`btn btn-sm rounded-pill px-3 ${
              activePill === cat ? "btn-dark" : "btn-outline-secondary"
            }`}
            style={{ whiteSpace: "nowrap", fontSize: "13px" }}
          >
            {screenLabels?.[normalizeKey(cat)]?.label_value ||
              filterDisplayLabels[cat] ||
              cat}
          </button>
        ))}
      </div>

      <div className="">
        {groupedCards.map(([cat, cards]) => (
          <div key={cat} className="mb-3">
            {activePill === "all" && (
              <h6 className="fw-semibold mb-2 mt-2">
                {screenLabels?.[normalizeKey(cat)]?.label_value ||
                  filterDisplayLabels[cat] ||
                  cat}
              </h6>
            )}
            {cards.map((c) => renderCard(c, cat))}
          </div>
        ))}
        {visibleCards.length === 0 && (
          <p className="text-center text-muted mt-4">
            {screenLabels?.no_results?.label_value || "No results to display."}
          </p>
        )}
      </div>

      <HealthDetailDrawer
        show={showCardDetail}
        onClose={() => setShowCardDetail(false)}
        activeTab={activeTab}
        showIconAndImage={true}
        metricData={selectedMetricData}
      />
    </div>
  );
};

export default ShowAllResult2;
